import { api } from 'pages/APIs';
import AbstractApiService from 'src/services/abstractApiService';
import type { dynamicObj } from 'src/pages/assets';
import type { AdvancedTestRequest, AdvancedTestResponse } from 'src/pages/WorkflowBuilder/workflow-advanced-models';

export default class WorkflowApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async getWorkflows(searchBy: string): Promise<any> {
    let queryParam = '';
    if (searchBy) {
      queryParam = '?name=' + searchBy;
    }
    return super.get(api.workflow_dashboard + queryParam);
  }

  public async getWorkflowDashboard(data: any): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.workflow_dashboard + '?' + querystring, {});
  }

  public async getWorkflowById(data: any) {
    return super.get(api.get_workflow_by_id);
  }

  // public async createWorkflow(data: any) {

  //   const dataObj: any = {
  //     'name': data['workflow_name'],
  //     'enabled': data['is_active'],
  //     'doc_type': data['doc_type'],
  //     'trigger': data['trigger'],
  //     'description': data['description'],
  //   }

  //   if(data['sub_org_id']) {
  //     dataObj['sub_org_id'] = data['sub_org_id'];
  //   }

  //   return super.post(api.workflow_create, dataObj );
  // }

  // public async updateWorkflow(data: any) {

  //   const dataObj: any = {
  //     'id': data['id'],
  //     'name': data['workflow_name'],
  //     'enabled': data['is_active'],
  //     'doc_type': data['doc_type'],
  //     'trigger': data['trigger'],
  //     'description': data['description'],
  //   }

  //   if(data['sub_org_id']) {
  //     dataObj['sub_org_id'] = data['sub_org_id'];
  //   }

  //   return super.put(api.workflow_update, dataObj );
  // }

  public async createWorkflow(data: any) {
    return super.post(api.workflow_create, data);
  }

  public async updateWorkflow(data: any) {
    return super.put(api.workflow_update, data);
  }

  public async saveWorkflowJson(workflowJson: any) {
    return super.post(api.save_workflow_json, workflowJson);
  }

  public async deleteWorkflow(id: string) {
    return super.delete(api.workflow_delete + id, {});
  }

  public async bulkDeleteWorkflows(ids: string[]) {
    return super.post(api.workflow_bulk_delete, { ids });
  }

  public async bulkToggleWorkflows(ids: string[], enabled: boolean) {
    return super.post(api.workflow_bulk_toggle, { ids, enabled });
  }

  public async copyWorkflow(id: string) {
    return super.post(api.copy_workflow + id, new FormData());
  }

  public async getWorkflowJson(workflowId: string) {
    return super.get(api.get_workflow_json + '?workflow_id=' + workflowId);
  }

  public async loadworkflowJson(workflowId: string) {
    return super.get(api.workflow_load + '?workflow_id=' + workflowId);
  }

  // public async saveWorkflowJson(workflowId: string, workflowJson: any) {

  //   return super.post(api.save_workflow_json + '?workflow_id=' + workflowId, workflowJson );
  // }

  public async exportworkflowtojson(workflowId: string) {
    return super.get(api.get_export_workflow_to_json + workflowId);
  }

  public async importWorkflowJson(formData: FormData): Promise<any> {
    return super.post(api.post_import_workflow_json, formData);
  }

  public async getDocumentTriggers() {
    return super.get(api.load_document_triggers);
  }

  public async getDocumentStatuses() {
    return super.get(api.load_document_statuses);
  }

  public async getWorkflowWhenCards() {
    return super.get(api.workflow_when);
  }

  public async getWorkflowAndCards() {
    return super.get(api.workflow_and);
  }

  public async getWorkflowThenCards() {
    return super.get(api.workflow_then);
  }

  public async getWorkflowAllCards() {
    return super.get(api.workflow_all_cards);
  }

  public async getDocFlowVersion(): Promise<any> {
    return super.get(api.docflow_version);
  }

  public async getCardTemplate(card_id: string): Promise<any> {
    return super.get(api.get_card_template + card_id);
  }

  public async triggerWorkflow(docId: string, data = {} as dynamicObj) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.trigger_workflow + '?doc_id=' + docId, formData);
  }

  public async triggerWorkflowOnFieldChange(data = {} as dynamicObj) {
    const formData = new FormData();
    const querystring = this.encodeQueryData(data);
    return super.post(
      api.trigger_workflow_field_change + '?' + querystring,
      formData,
    );
  }

  public async runCardForDocument(
    card_data = {} as dynamicObj,
    params = {} as dynamicObj,
  ) {
    const querystring = this.encodeQueryData(params);
    return super.post(api.run_workflow_card + '?' + querystring, card_data);
  }

  public async getWorkflowLogsForDoc(
    doc_id: string,
    data = {} as dynamicObj,
  ): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_workflow_logs + doc_id + '?' + querystring);
  }

  public async saveWorkflowTemplateJSON(templateJSON: any): Promise<any> {
    return super.post(api.wf_save_template, templateJSON);
  }
  public async loadWorkflowTemplateJson(workflowId: string) {
    return super.get(api.load_wf_template + '?template_id=' + workflowId);
  }
  public async getTemplateDashboard(data: any): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.workflow_template_dashboard + '?' + querystring, {});
  }
  public async deleteWorkflowTemplate(templateId: string) {
    return super.delete(api.delete_wf_template + templateId, {});
  }
  public async copyTemplate(templateId: string) {
    return super.post(api.copy_workflow_template + templateId, {});
  }

  // Advanced workflow methods
  public async saveAdvancedWorkflow(data: any): Promise<any> {
    return super.post(api.advanced_save, data);
  }

  public async loadAdvancedWorkflow(workflowId: string): Promise<any> {
    return super.get(api.advanced_load + workflowId);
  }

  public async validateAdvancedWorkflow(data: any): Promise<any> {
    return super.post(api.advanced_validate, data);
  }

  public async convertToAdvanced(workflowId: string): Promise<any> {
    return super.post(api.advanced_convert + workflowId, {});
  }

  public async testAdvancedWorkflow(payload: AdvancedTestRequest): Promise<AdvancedTestResponse> {
    return super.post(api.advanced_test, payload);
  }

  public async saveAdvancedAsTemplate(data: any): Promise<any> {
    return super.post(api.advanced_save_as_template, data);
  }

  public async getAdvancedTemplates(
    limit = 20,
    offset = 0,
    search = ''
  ): Promise<any> {
    return super.get(
      api.advanced_templates + `?limit=${limit}&offset=${offset}&search=${search}`
    );
  }

  public async loadAdvancedTemplate(templateId: string): Promise<any> {
    return super.get(api.advanced_templates + '/' + templateId);
  }

  public async createFromAdvancedTemplate(templateId: string): Promise<any> {
    return super.post(api.advanced_create_from_template + templateId, {});
  }

  public async deleteAdvancedTemplate(templateId: string): Promise<any> {
    return super.delete(api.advanced_templates + '/' + templateId, {});
  }

  public async getWorkflowsForOrg(): Promise<any> {
    return super.get(api.get_workflows_for_org);
  }

  public async getWorkflowDashboardSummary(params: any): Promise<any> {
    const queryParams: any = {
      include_today: true, // Always include today for executed_today count
    };

    // Add compare mode support
    if (params.compare_mode) {
      queryParams.compare_mode = true;
    }

    if (params.workflow_id) {
      queryParams.id = params.workflow_id;
    }

    const body: any = {};

    if (params.main) {
      body.main = params.main;
    } else if (params.start_date && params.end_date) {
      // Backward compatibility for simple date range
      body.main = {
        start_date: params.start_date,
        end_date: params.end_date,
      };
    }

    if (params.compare) {
      body.compare = params.compare;
    }

    const querystring = this.encodeQueryData(queryParams);
    return super.post(
      api.workflow_dashboard_summary + (querystring ? '?' + querystring : ''),
      body,
    );
  }

  // public async getWorkflowRecentRuns(data: any): Promise<any> {
  //   const querystring = this.encodeQueryData(data);
  //   return super.get(api.workflow_logs_recent + '?' + querystring);
  // }

  public async getWorkflowCreationSummary(data: any): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.workflow_creation_summary + '?' + querystring);
  }

  public async getLicenseStatus(): Promise<any> {
    return super.get(api.license_status);
  }

  public async toggleLicense(appKey: string, enabled: boolean): Promise<any> {
    return super.post(api.license_toggle, { app_key: appKey, enabled });
  }
}
